// src/pages/HomePage.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import SEO, { SITE_URL, SITE_NAME, itemListLd } from '../components/common/SEO';
import Hero from '../components/common/Hero';
import ChipFilter from '../components/common/ChipFilter';
import Sidebar from '../components/common/Sidebar';
import ProgramCard from '../components/common/ProgramCard';
import InContentAd from '../components/ads/InContentAd';
import Icon from '../components/common/Icon';
import { supportPrograms, getPopularPrograms, getPopularTags } from '../data/supportPrograms';
import { changes2026, keyFigures2026 } from '../data/changes2026';
import { outlookItems, budget2027 } from '../data/outlook2027';
import { categories } from '../data/categories';

const websiteLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    url: SITE_URL,
    name: SITE_NAME,
    alternateName: '고브인포 정부지원금 정보센터',
    description:
        '2026년 대한민국 정부지원금 정보와 2027년 정부 예산안 전망을 분야별로 정리한 정보 서비스',
    inLanguage: 'ko-KR',
    publisher: { '@id': `${SITE_URL}/#organization` },
    potentialAction: {
        '@type': 'SearchAction',
        target: { '@type': 'EntryPoint', urlTemplate: `${SITE_URL}/search?q={search_term_string}` },
        'query-input': 'required name=search_term_string',
    },
};

const organizationLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: SITE_NAME,
    url: SITE_URL,
    logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo192.png`, width: 192, height: 192 },
    description: '대한민국 정부지원금 정보를 정리해 제공하는 민간 정보 서비스',
};

/** 추천 카드에는 금액의 첫 구간만 크게 보여 카드 높이를 고르게 유지합니다 */
const headlineAmount = (amount: string) => amount.split(' · ')[0];

const HomePage: React.FC = () => {
    const popular = getPopularPrograms(6);
    const [lead, ...rest] = popular;
    const tags = getPopularTags(16);
    const highlightChanges = changes2026.slice(0, 4);
    const outlookPreview = outlookItems.slice(0, 3);

    return (
        <>
            <SEO
                path="/"
                title="2026년 정부지원금 총정리 | 고브인포 정부지원금 정보센터"
                description="2026년 정부지원금을 한눈에. 근로장려금 최대 330만원, 기초연금 월 349,700원, 생계급여 4인 207만원 등 자격요건·지원금액·신청방법을 분야별로 정리하고 2027년 예산안 전망까지 안내합니다."
                keywords="정부지원금, 2026 정부지원금, 2027 정부지원금, 근로장려금, 기초연금, 청년월세지원, 국가장학금, 생계급여, 주거급여, 부모급여, 첫만남이용권, 실업급여, 지원금 신청방법"
                updated="2026-09-02"
                jsonLd={[
                    websiteLd,
                    organizationLd,
                    itemListLd(
                        '2026년 인기 정부지원금',
                        popular.map(p => ({ title: p.title, path: `/program/${p.slug}` }))
                    ),
                ]}
            />

            <div className="gh-layout">
                <div>
                    <Hero
                        eyebrow="2026년 9월 기준 최신 정보"
                        title={
                            <>
                                2026년 정부지원금,
                                <br />
                                나에게 맞는 혜택을 찾으세요
                            </>
                        }
                        description={
                            <>
                                근로·주거·출산육아·교육·창업·복지까지 <strong>{supportPrograms.length}개</strong> 지원 제도의
                                자격요건과 신청방법을 정리했습니다.
                            </>
                        }
                        stats={keyFigures2026.slice(0, 3).map(f => ({
                            value: f.value,
                            label: f.label,
                        }))}
                        art="documents"
                    />

                    <ChipFilter />

                    {/* 많이 찾는 지원금 */}
                    <section className="gh-sec">
                        <div className="gh-sec__head">
                            <div>
                                <h2 className="gh-sec__title">
                                    <Icon name="star" size={19} />
                                    지금 많이 찾는 지원금
                                </h2>
                                <p className="gh-sec__desc">2026년 기준 신청자가 가장 많은 제도입니다</p>
                            </div>
                        </div>

                        <div className="gh-featured">
                            {lead && (
                                <Link to={`/program/${lead.slug}`} className="gh-feat gh-feat--lead">
                                    <span className="gh-feat__tag">가장 많이 찾는 지원금</span>
                                    <h3 className="gh-feat__title">{lead.title}</h3>
                                    <p className="gh-feat__amount">{headlineAmount(lead.amount)}</p>
                                    <p className="gh-feat__desc">{lead.summary}</p>
                                    <div className="gh-feat__meta">
                                        {lead.tags.slice(0, 3).map(t => (
                                            <span className="gh-feat__hash" key={t}>
                                                #{t}
                                            </span>
                                        ))}
                                    </div>
                                    <span className="gh-feat__cta">
                                        자세히 보기
                                        <Icon name="arrowRight" size={14} strokeWidth={2.2} />
                                    </span>
                                </Link>
                            )}

                            {rest.slice(0, 2).map((p, i) => (
                                <Link to={`/program/${p.slug}`} className="gh-feat" key={p.id}>
                                    <span
                                        className={`gh-feat__tag gh-feat__tag--${
                                            ['soft', 'emerald', 'amber', 'pink'][i % 4]
                                        }`}
                                    >
                                        {p.agency}
                                    </span>
                                    <h3 className="gh-feat__title">{p.title}</h3>
                                    <p className="gh-feat__desc">{p.summary}</p>
                                    <div className="gh-card__facts">
                                        <div className="gh-fact">
                                            <span className="gh-fact__k">지원금액</span>
                                            <span className="gh-fact__v gh-fact__v--amount">
                                                {headlineAmount(p.amount)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="gh-feat__meta">
                                        {p.tags.slice(0, 2).map(t => (
                                            <span className="gh-feat__hash" key={t}>
                                                #{t}
                                            </span>
                                        ))}
                                    </div>
                                </Link>
                            ))}
                        </div>

                        <div className="gh-cards">
                            {rest.slice(2).map(p => (
                                <ProgramCard key={p.id} program={p} />
                            ))}
                        </div>
                    </section>

                    {/* 2026년 달라진 점 */}
                    <section className="gh-sec">
                        <div className="gh-sec__head">
                            <div>
                                <h2 className="gh-sec__title">
                                    <Icon name="sparkle" size={19} />
                                    2026년, 이렇게 <em>달라졌습니다</em>
                                </h2>
                                <p className="gh-sec__desc">올해 확정된 기준 변경 사항</p>
                            </div>
                            <Link to="/2026" className="gh-sec__more">
                                전체 보기
                                <Icon name="chevronRight" size={13} strokeWidth={2.4} />
                            </Link>
                        </div>

                        <div className="gh-ba">
                            {highlightChanges.map(c => (
                                <article className="gh-ba__item" key={c.id}>
                                    <h3 className="gh-ba__title">{c.title}</h3>
                                    <div className="gh-ba__row">
                                        <div className="gh-ba__cell gh-ba__cell--before">
                                            <b>2025년</b>
                                            {c.before}
                                        </div>
                                        <Icon name="arrowRight" size={18} className="gh-ba__arrow" />
                                        <div className="gh-ba__cell gh-ba__cell--after">
                                            <b>2026년</b>
                                            {c.after}
                                        </div>
                                    </div>
                                    <p className="gh-ba__detail">{c.detail}</p>
                                </article>
                            ))}
                        </div>
                    </section>

                    <InContentAd />

                    {/* 분야별 안내 */}
                    <section className="gh-sec">
                        <div className="gh-sec__head">
                            <div>
                                <h2 className="gh-sec__title">
                                    <Icon name="grid" size={19} />
                                    분야별 지원금 안내
                                </h2>
                                <p className="gh-sec__desc">내 상황에 맞는 분야를 선택하세요</p>
                            </div>
                        </div>

                        <div className="gh-cards">
                            {categories.slice(1).map(c => {
                                const count = supportPrograms.filter(p => p.category === c.id).length;
                                return (
                                    <Link to={c.path} className="gh-card" key={c.id}>
                                        <div className="gh-card__top">
                                            <span className="gh-card__cat">{count}개 제도</span>
                                        </div>
                                        <h3 className="gh-card__title">{c.name}</h3>
                                        <p className="gh-card__summary">{c.description}</p>
                                        <div className="gh-card__foot">
                                            <span className="gh-card__agency">{c.tagline}</span>
                                            <span className="gh-card__link">
                                                바로가기
                                                <Icon name="chevronRight" size={14} strokeWidth={2.2} />
                                            </span>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    </section>

                    {/* 2027 전망 미리보기 */}
                    <section className="gh-sec">
                        <div className="gh-sec__head">
                            <div>
                                <h2 className="gh-sec__title">
                                    <Icon name="chart" size={19} />
                                    2027년 정부지원금 <em>전망</em>
                                </h2>
                                <p className="gh-sec__desc">
                                    {budget2027.announcedAt.replace(/-/g, '.')} 발표된 정부 예산안 기준
                                </p>
                            </div>
                            <Link to="/2027" className="gh-sec__more">
                                전체 보기
                                <Icon name="chevronRight" size={13} strokeWidth={2.4} />
                            </Link>
                        </div>

                        <div className="gh-outlook">
                            {outlookPreview.map(o => (
                                <article className="gh-outlook__item" key={o.id}>
                                    <div className="gh-outlook__figure">
                                        <div className="gh-outlook__fig">{o.figure}</div>
                                        <div className="gh-outlook__figlabel">{o.figureLabel}</div>
                                    </div>
                                    <div className="gh-outlook__main">
                                        <h3 className="gh-outlook__title">
                                            {o.title}
                                            <span className={`gh-status gh-status--${o.status}`}>{o.status}</span>
                                        </h3>
                                        <p className="gh-outlook__detail">{o.summary}</p>
                                    </div>
                                </article>
                            ))}
                        </div>
                    </section>

                    {/* 인기 검색어 */}
                    <section className="gh-sec">
                        <div className="gh-sec__head">
                            <h2 className="gh-sec__title">
                                <Icon name="search" size={19} />
                                인기 검색 키워드
                            </h2>
                        </div>
                        <div className="gh-tags">
                            {tags.map(t => (
                                <Link key={t} to={`/search?q=${encodeURIComponent(t)}`} className="gh-tag">
                                    # {t}
                                </Link>
                            ))}
                        </div>
                    </section>

                </div>

                <Sidebar />
            </div>
        </>
    );
};

export default HomePage;
